// src/pages/PriceQuotes/PriceQuoteDetail.jsx

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { doc, onSnapshot, updateDoc, collection, query, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../context/AuthContext';
import { generatePriceQuotePDF } from '../../utils/pdfGenerator';

// --- STYLES ---
const styles = {
    container: { fontFamily: '"Inter", sans-serif', maxWidth: '1100px', margin: '0 auto', color: '#1f2937' },
    header: { marginBottom: '32px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px' },
    titleArea: {},
    title: { fontSize: '28px', fontWeight: '800', color: '#111827', margin: 0, letterSpacing: '-0.5px', display: 'flex', alignItems: 'center', gap: '12px' },
    subtitle: { fontSize: '14px', color: '#6b7280', marginTop: '4px' },
    headerActions: { display: 'flex', gap: '10px', flexWrap: 'wrap' },
    backBtn: {
        padding: '10px 20px', border: '1px solid #e2e8f0', background: 'white', color: '#6b7280',
        cursor: 'pointer', fontSize: '14px', fontWeight: '500', borderRadius: '10px',
        display: 'flex', alignItems: 'center', gap: '6px', transition: 'all 0.2s'
    },
    pdfBtn: {
        padding: '10px 20px', border: 'none', background: 'linear-gradient(135deg, #f59e0b, #d97706)',
        color: 'white', cursor: 'pointer', fontSize: '14px', fontWeight: '600', borderRadius: '10px',
        display: 'flex', alignItems: 'center', gap: '6px', transition: 'all 0.2s',
        boxShadow: '0 2px 8px rgba(245,158,11,0.3)'
    },
    editBtn: {
        padding: '10px 20px', border: 'none', background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
        color: 'white', cursor: 'pointer', fontSize: '14px', fontWeight: '600', borderRadius: '10px',
        display: 'flex', alignItems: 'center', gap: '6px', transition: 'all 0.2s',
        boxShadow: '0 2px 8px rgba(59,130,246,0.3)'
    },
    saveBtn: {
        padding: '10px 20px', border: 'none', background: 'linear-gradient(135deg, #10b981, #059669)',
        color: 'white', cursor: 'pointer', fontSize: '14px', fontWeight: '600', borderRadius: '10px',
        display: 'flex', alignItems: 'center', gap: '6px', transition: 'all 0.2s',
        boxShadow: '0 2px 8px rgba(16,185,129,0.3)'
    },
    cancelBtn: {
        padding: '10px 20px', border: '1px solid #fca5a5', background: 'white', color: '#ef4444',
        cursor: 'pointer', fontSize: '14px', fontWeight: '500', borderRadius: '10px',
        display: 'flex', alignItems: 'center', gap: '6px', transition: 'all 0.2s'
    },

    box: {
        background: 'white', padding: '32px', borderRadius: '16px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid #f1f5f9', marginBottom: '24px'
    },
    infoGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px' },
    infoItem: { display: 'flex', flexDirection: 'column', gap: '4px' },
    infoLabel: { fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px' },
    infoValue: { fontSize: '15px', fontWeight: '500', color: '#111827' },

    input: {
        padding: '10px 14px', border: '1px solid #e2e8f0', borderRadius: '8px',
        fontSize: '14px', width: '100%', boxSizing: 'border-box', outline: 'none',
        transition: 'all 0.2s', backgroundColor: '#f8fafc'
    },
    select: {
        padding: '10px 14px', border: '1px solid #e2e8f0', borderRadius: '8px',
        fontSize: '14px', width: '100%', boxSizing: 'border-box', outline: 'none',
        backgroundColor: '#f8fafc', appearance: 'none',
        backgroundImage: 'url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'currentColor\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3e%3cpolyline points=\'6 9 12 15 18 9\'%3e%3c/polyline%3e%3c/svg%3e")',
        backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center', backgroundSize: '14px'
    },
    textarea: {
        padding: '10px 14px', border: '1px solid #e2e8f0', borderRadius: '8px',
        fontSize: '14px', width: '100%', boxSizing: 'border-box', outline: 'none',
        transition: 'all 0.2s', backgroundColor: '#f8fafc', resize: 'vertical',
        minHeight: '60px', fontFamily: '"Inter", sans-serif'
    },
    inlineInput: {
        padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: '6px',
        fontSize: '14px', outline: 'none', width: '100%', boxSizing: 'border-box',
        backgroundColor: '#fff', textAlign: 'right'
    },

    sectionHeader: {
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: '16px', borderBottom: '2px solid #f1f5f9', paddingBottom: '12px'
    },
    sectionTitle: { fontSize: '16px', fontWeight: '700', color: '#111827', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' },

    productRow: {
        display: 'grid', gridTemplateColumns: '2.5fr 1fr 1fr 1fr 1fr auto',
        gap: '12px', alignItems: 'end', background: '#f8fafc', padding: '16px',
        borderRadius: '12px', border: '1px solid #e2e8f0', marginBottom: '16px'
    },
    formGroup: { display: 'flex', flexDirection: 'column', gap: '6px' },
    label: { fontSize: '12px', fontWeight: '600', color: '#374151', textTransform: 'uppercase', letterSpacing: '0.5px' },
    addBtn: {
        padding: '10px 20px', border: 'none', borderRadius: '10px', cursor: 'pointer',
        fontSize: '14px', fontWeight: '600', display: 'flex', alignItems: 'center',
        gap: '6px', background: '#3b82f6', color: 'white', height: '42px',
        transition: 'all 0.2s', boxShadow: '0 2px 4px rgba(59,130,246,0.2)'
    },

    tableContainer: { border: '1px solid #e5e7eb', borderRadius: '12px', overflow: 'hidden', marginBottom: '24px' },
    table: { width: '100%', borderCollapse: 'collapse' },
    th: {
        background: '#f8fafc', padding: '12px 14px', textAlign: 'left',
        borderBottom: '1px solid #e5e7eb', fontSize: '11px', fontWeight: '600',
        color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px'
    },
    td: { padding: '14px', borderBottom: '1px solid #f3f4f6', fontSize: '14px', color: '#1f2937' },
    deleteBtn: {
        background: 'white', border: '1px solid #fee2e2', color: '#ef4444', cursor: 'pointer',
        padding: '6px', borderRadius: '6px', display: 'flex', alignItems: 'center',
        justifyContent: 'center', transition: 'all 0.2s', width: '32px', height: '32px'
    },

    badge: {
        padding: '6px 16px', borderRadius: '20px', fontSize: '13px',
        fontWeight: '600', display: 'inline-block'
    },

    totalsWrapper: { display: 'flex', justifyContent: 'flex-end', marginTop: '24px' },
    totalsBox: {
        width: '380px', padding: '24px', background: 'linear-gradient(135deg, #f8fafc, #f1f5f9)',
        borderRadius: '16px', border: '1px solid #e2e8f0'
    },
    totalRow: { display: 'flex', justifyContent: 'space-between', marginBottom: '10px', fontSize: '14px', color: '#4b5563', alignItems: 'center' },
    totalLabel: { fontWeight: '500' },
    grandTotalRow: {
        display: 'flex', justifyContent: 'space-between', marginTop: '14px',
        paddingTop: '14px', borderTop: '2px solid #cbd5e1'
    },
    grandTotalLabel: { fontSize: '16px', fontWeight: '700', color: '#111827' },
    grandTotalValue: { fontSize: '22px', fontWeight: '800', color: '#059669' },

    statusBar: { display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '16px' },
    statusBtn: {
        padding: '8px 16px', border: '1px solid #e2e8f0', background: 'white',
        borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '500',
        transition: 'all 0.2s'
    },
    notesBox: {
        background: '#fefce8', padding: '16px 20px', borderRadius: '12px',
        border: '1px solid #fef08a', marginTop: '20px', fontSize: '14px', color: '#854d0e',
        lineHeight: '1.6'
    },
    editBanner: {
        background: 'linear-gradient(135deg, #dbeafe, #ede9fe)', padding: '12px 20px',
        borderRadius: '12px', marginBottom: '24px', display: 'flex', alignItems: 'center',
        gap: '10px', fontSize: '14px', fontWeight: '500', color: '#4338ca',
        border: '1px solid #c7d2fe'
    },
    // Customer search autocomplete
    autocompleteWrapper: { position: 'relative' },
    autocompleteDropdown: {
        position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50,
        background: 'white', border: '1px solid #e2e8f0', borderRadius: '10px',
        boxShadow: '0 8px 24px rgba(0,0,0,0.12)', maxHeight: '220px', overflowY: 'auto',
        marginTop: '4px'
    },
    autocompleteItem: {
        padding: '10px 16px', cursor: 'pointer', fontSize: '14px',
        borderBottom: '1px solid #f3f4f6', transition: 'background 0.15s'
    },
    autocompleteEmpty: {
        padding: '12px 16px', fontSize: '13px', color: '#9ca3af', fontStyle: 'italic'
    },
    selectedBadge: {
        display: 'inline-flex', alignItems: 'center', gap: '8px',
        background: '#ede9fe', color: '#6d28d9', padding: '8px 14px',
        borderRadius: '10px', fontSize: '14px', fontWeight: '500'
    },
    clearBtn: {
        background: 'none', border: 'none', cursor: 'pointer', color: '#6d28d9',
        fontSize: '16px', padding: '0 2px', lineHeight: 1
    }
};

const statusConfig = {
    'Taslak': { bg: '#f1f5f9', color: '#475569' },
    'Gönderildi': { bg: '#dbeafe', color: '#1d4ed8' },
    'Onaylandı': { bg: '#dcfce7', color: '#06C167' },
    'Reddedildi': { bg: '#fee2e2', color: '#dc2626' },
};

function PriceQuoteDetail() {
    const { quoteId } = useParams();
    const { userData } = useAuth();
    const navigate = useNavigate();
    const companyId = userData?.companyId;

    const [quote, setQuote] = useState(null);
    const [companyInfo, setCompanyInfo] = useState(null);
    const [products, setProducts] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(true);

    // Edit mode state
    const [isEditing, setIsEditing] = useState(false);
    const [editData, setEditData] = useState(null);
    const [currentItem, setCurrentItem] = useState({ productId: '', quantity: 1, unitPrice: '', discount: 0 });
    const [saving, setSaving] = useState(false);
    const [editCustomerSearch, setEditCustomerSearch] = useState('');
    const [showEditCustomerDropdown, setShowEditCustomerDropdown] = useState(false);
    const editCustomerSearchRef = useRef(null);
    const [productSearch, setProductSearch] = useState('');
    const [showProductDropdown, setShowProductDropdown] = useState(false);

    // Fetch quote
    useEffect(() => {
        if (!companyId || !quoteId) return;
        const unsub = onSnapshot(
            doc(db, 'companies', companyId, 'priceQuotes', quoteId),
            snap => {
                if (snap.exists()) {
                    setQuote({ id: snap.id, ...snap.data() });
                }
                setLoading(false);
            }
        );
        return () => unsub();
    }, [companyId, quoteId]);

    // Fetch company info for PDF
    useEffect(() => {
        if (!companyId) return;
        const unsub = onSnapshot(doc(db, 'companies', companyId), snap => {
            if (snap.exists()) setCompanyInfo(snap.data());
        });
        return () => unsub();
    }, [companyId]);

    // Fetch products & customers for edit mode
    useEffect(() => {
        if (!companyId) return;
        const unsubProducts = onSnapshot(
            query(collection(db, 'companies', companyId, 'products')),
            snap => setProducts(snap.docs.map(d => ({ id: d.id, ...d.data() })))
        );
        const unsubCustomers = onSnapshot(
            query(collection(db, 'companies', companyId, 'customers')),
            snap => setCustomers(snap.docs.map(d => ({ id: d.id, ...d.data() })))
        );
        return () => { unsubProducts(); unsubCustomers(); };
    }, [companyId]);

    const formatDate = (date) => {
        if (!date) return '-';
        const d = date.seconds ? new Date(date.seconds * 1000) : new Date(date);
        return d.toLocaleDateString('tr-TR');
    };

    const toInputDate = (date) => {
        if (!date) return '';
        const d = date.seconds ? new Date(date.seconds * 1000) : new Date(date);
        return d.toISOString().split('T')[0];
    };

    const getCurrencySymbol = (currency) => {
        if (currency === 'USD') return '$';
        if (currency === 'EUR') return '€';
        return '₺';
    };

    // --- EDIT MODE ---
    const startEditing = () => {
        setEditData({
            customerId: quote.customerId || '',
            customerName: quote.customerName || '',
            quoteDate: toInputDate(quote.quoteDate),
            validUntil: toInputDate(quote.validUntil),
            currency: quote.currency || 'TRY',
            notes: quote.notes || '',
            vatRate: quote.vatRate || 0,
            discountRate: quote.discountRate || 0,
            lineItems: [...(quote.lineItems || [])],
            customerAddress: quote.customerAddress || '',
            customerTaxOffice: quote.customerTaxOffice || '',
            customerTaxNumber: quote.customerTaxNumber || '',
        });
        setEditCustomerSearch('');
        setIsEditing(true);
    };

    const cancelEditing = () => {
        setIsEditing(false);
        setEditData(null);
        setCurrentItem({ productId: '', quantity: 1, unitPrice: '', discount: 0 });
        setEditCustomerSearch('');
    };

    const handleEditFieldChange = (field, value) => {
        setEditData(prev => {
            const updated = { ...prev, [field]: value };
            // If customer changed, update customerName
            if (field === 'customerId') {
                const customer = customers.find(c => c.id === value);
                updated.customerName = customer?.name || '';
                updated.customerAddress = customer?.address || '';
                updated.customerTaxOffice = customer?.taxOffice || '';
                updated.customerTaxNumber = customer?.taxNumber || '';
            }
            return updated;
        });
    };

    // When product selection changes in edit mode
    useEffect(() => {
        if (currentItem.productId && isEditing) {
            const product = products.find(p => p.id === currentItem.productId);
            if (product) {
                setCurrentItem(prev => ({ ...prev, unitPrice: product.salePrice || 0 }));
            }
        }
    }, [currentItem.productId, products, isEditing]);

    const handleAddEditItem = () => {
        if (!currentItem.productId || currentItem.quantity <= 0) return;
        const product = products.find(p => p.id === currentItem.productId);
        if (!product) return;

        const qty = Number(currentItem.quantity);
        const price = Number(currentItem.unitPrice) || 0;
        const disc = Number(currentItem.discount) || 0;
        const lineTotal = qty * price * (1 - disc / 100);

        setEditData(prev => ({
            ...prev,
            lineItems: [...prev.lineItems, {
                productId: product.id,
                name: product.name,
                quantity: qty,
                unitPrice: price,
                discount: disc,
                total: lineTotal
            }]
        }));
        setCurrentItem({ productId: '', quantity: 1, unitPrice: '', discount: 0 });
        setProductSearch('');
    };

    const handleRemoveEditItem = (index) => {
        setEditData(prev => ({
            ...prev,
            lineItems: prev.lineItems.filter((_, i) => i !== index)
        }));
    };

    const handleEditLineItemChange = (index, field, value) => {
        setEditData(prev => {
            const items = [...prev.lineItems];
            items[index] = { ...items[index], [field]: Number(value) || 0 };
            items[index].total = items[index].quantity * items[index].unitPrice * (1 - (items[index].discount || 0) / 100);
            return { ...prev, lineItems: items };
        });
    };

    // Edit calculations
    const editSubTotal = editData ? editData.lineItems.reduce((sum, item) => sum + (item.total || 0), 0) : 0;
    const editDiscountAmount = editSubTotal * ((editData?.discountRate || 0) / 100);
    const editAfterDiscount = editSubTotal - editDiscountAmount;
    const editVatAmount = editAfterDiscount * ((editData?.vatRate || 0) / 100);
    const editGrandTotal = editAfterDiscount + editVatAmount;

    const handleSaveEdit = async () => {
        if (!editData.customerId) {
            alert('Lütfen bir müşteri seçin.');
            return;
        }
        if (editData.lineItems.length === 0) {
            alert('Lütfen en az bir ürün ekleyin.');
            return;
        }

        setSaving(true);
        try {
            await updateDoc(doc(db, 'companies', companyId, 'priceQuotes', quoteId), {
                customerId: editData.customerId,
                customerName: editData.customerName,
                customerAddress: editData.customerAddress || '',
                customerTaxOffice: editData.customerTaxOffice || '',
                customerTaxNumber: editData.customerTaxNumber || '',
                quoteDate: Timestamp.fromDate(new Date(editData.quoteDate)),
                validUntil: Timestamp.fromDate(new Date(editData.validUntil)),
                currency: editData.currency,
                notes: editData.notes,
                vatRate: editData.vatRate,
                discountRate: editData.discountRate,
                lineItems: editData.lineItems,
                subTotal: editSubTotal,
                discountAmount: editDiscountAmount,
                vatAmount: editVatAmount,
                total: editGrandTotal,
                updatedAt: Timestamp.now()
            });
            setIsEditing(false);
            setEditData(null);
        } catch (error) {
            console.error('Teklif güncellenirken hata:', error);
            alert('Teklif güncellenirken bir hata oluştu.');
        } finally {
            setSaving(false);
        }
    };

    const handleStatusChange = async (newStatus) => {
        try {
            await updateDoc(doc(db, 'companies', companyId, 'priceQuotes', quoteId), {
                status: newStatus,
                updatedAt: Timestamp.now()
            });
        } catch (error) {
            console.error('Durum güncellenirken hata:', error);
            alert('Durum güncellenirken bir hata oluştu.');
        }
    };

    const handleDownloadPDF = async () => {
        if (!quote || !companyInfo) {
            alert('PDF verileri yükleniyor...');
            return;
        }
        try {
            await generatePriceQuotePDF(companyInfo, quote, products);
        } catch (error) {
            console.error('PDF oluşturulurken hata:', error);
            alert('PDF oluşturulurken bir hata oluştu.');
        }
    };

    if (loading) {
        return <div style={styles.container}><div style={{ textAlign: 'center', padding: '60px', color: '#6b7280' }}>Yükleniyor...</div></div>;
    }

    if (!quote) {
        return (
            <div style={styles.container}>
                <div style={{ textAlign: 'center', padding: '60px', color: '#6b7280' }}>
                    <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔍</div>
                    <div>Teklif bulunamadı.</div>
                    <button onClick={() => navigate('/fiyat-teklifleri')} style={{ ...styles.backBtn, marginTop: '16px', display: 'inline-flex' }}>← Tekliflere Dön</button>
                </div>
            </div>
        );
    }

    const sym = getCurrencySymbol(isEditing ? editData.currency : quote.currency);
    const sc = statusConfig[quote.status] || statusConfig['Taslak'];

    return (
        <div style={styles.container}>
            {/* Header */}
            <div style={styles.header}>
                <div style={styles.titleArea}>
                    <h1 style={styles.title}>
                        {quote.quoteNumber || 'Teklif'}
                        <span style={{ ...styles.badge, backgroundColor: sc.bg, color: sc.color, fontSize: '14px' }}>
                            {quote.status}
                        </span>
                    </h1>
                    <p style={styles.subtitle}>{quote.customerName} • {formatDate(quote.quoteDate)}</p>
                </div>
                <div style={styles.headerActions}>
                    <button onClick={() => navigate('/fiyat-teklifleri')} style={styles.backBtn}>← Geri</button>
                    {isEditing ? (
                        <>
                            <button onClick={cancelEditing} style={styles.cancelBtn}>✕ İptal</button>
                            <button onClick={handleSaveEdit} disabled={saving} style={{ ...styles.saveBtn, opacity: saving ? 0.7 : 1 }}>
                                {saving ? '⏳ Kaydediliyor...' : '💾 Kaydet'}
                            </button>
                        </>
                    ) : (
                        <>
                            <button onClick={startEditing} style={styles.editBtn}>✏️ Düzenle</button>
                            <button onClick={handleDownloadPDF} style={styles.pdfBtn}>📄 PDF İndir</button>
                        </>
                    )}
                </div>
            </div>

            {isEditing && (
                <div style={styles.editBanner}>
                    ✏️ Düzenleme modundasınız. Değişikliklerinizi kaydetmek için "Kaydet" butonuna tıklayın.
                </div>
            )}

            {/* Genel Bilgiler */}
            <div style={styles.box}>
                <div style={styles.sectionHeader}>
                    <h3 style={styles.sectionTitle}>📝 Genel Bilgiler</h3>
                </div>

                {isEditing ? (
                    <div style={styles.infoGrid}>
                        <div style={styles.infoItem}>
                            <span style={styles.infoLabel}>Müşteri</span>
                            {editData.customerId ? (
                                <div style={styles.selectedBadge}>
                                    <span>👤 {editData.customerName || 'Müşteri'}</span>
                                    <button style={styles.clearBtn} onClick={() => { handleEditFieldChange('customerId', ''); handleEditFieldChange('customerName', ''); setEditCustomerSearch(''); }} title="Değiştir">✕</button>
                                </div>
                            ) : (
                                <div style={styles.autocompleteWrapper} ref={editCustomerSearchRef}>
                                    <input
                                        type="text"
                                        style={styles.input}
                                        placeholder="🔍 Müşteri adı yazarak arayın..."
                                        value={editCustomerSearch}
                                        onChange={e => { setEditCustomerSearch(e.target.value); setShowEditCustomerDropdown(true); }}
                                        onFocus={() => setShowEditCustomerDropdown(true)}
                                        onBlur={() => setTimeout(() => setShowEditCustomerDropdown(false), 200)}
                                    />
                                    {showEditCustomerDropdown && editCustomerSearch.length > 0 && (
                                        <div style={styles.autocompleteDropdown}>
                                            {customers.filter(c => c.name?.toLowerCase().includes(editCustomerSearch.toLowerCase())).length === 0 ? (
                                                <div style={styles.autocompleteEmpty}>Müşteri bulunamadı</div>
                                            ) : (
                                                customers.filter(c => c.name?.toLowerCase().includes(editCustomerSearch.toLowerCase())).map(c => (
                                                    <div
                                                        key={c.id}
                                                        style={styles.autocompleteItem}
                                                        onMouseDown={() => { handleEditFieldChange('customerId', c.id); setEditCustomerSearch(''); setShowEditCustomerDropdown(false); }}
                                                        onMouseEnter={e => e.target.style.backgroundColor = '#f5f3ff'}
                                                        onMouseLeave={e => e.target.style.backgroundColor = 'white'}
                                                    >
                                                        👤 {c.name}
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                        <div style={styles.infoItem}>
                            <span style={styles.infoLabel}>Teklif Tarihi</span>
                            <input type="date" style={styles.input} value={editData.quoteDate} onChange={e => handleEditFieldChange('quoteDate', e.target.value)} />
                        </div>
                        <div style={styles.infoItem}>
                            <span style={styles.infoLabel}>Geçerlilik Tarihi</span>
                            <input type="date" style={styles.input} value={editData.validUntil} onChange={e => handleEditFieldChange('validUntil', e.target.value)} />
                        </div>
                        <div style={styles.infoItem}>
                            <span style={styles.infoLabel}>Para Birimi</span>
                            <select style={styles.select} value={editData.currency} onChange={e => handleEditFieldChange('currency', e.target.value)}>
                                <option value="TRY">₺ TRY</option>
                                <option value="USD">$ USD</option>
                                <option value="EUR">€ EUR</option>
                            </select>
                        </div>
                        <div style={{ ...styles.infoItem, gridColumn: '1 / -1' }}>
                            <span style={styles.infoLabel}>Notlar</span>
                            <textarea style={styles.textarea} value={editData.notes} onChange={e => handleEditFieldChange('notes', e.target.value)} placeholder="Notlarınızı buraya yazın..." />
                        </div>
                    </div>
                ) : (
                    <>
                        <div style={styles.infoGrid}>
                            <div style={styles.infoItem}>
                                <span style={styles.infoLabel}>Teklif No</span>
                                <span style={styles.infoValue}>{quote.quoteNumber || '-'}</span>
                            </div>
                            <div style={styles.infoItem}>
                                <span style={styles.infoLabel}>Müşteri</span>
                                <span style={styles.infoValue}>{quote.customerName || '-'}</span>
                            </div>
                            <div style={styles.infoItem}>
                                <span style={styles.infoLabel}>Teklif Tarihi</span>
                                <span style={styles.infoValue}>{formatDate(quote.quoteDate)}</span>
                            </div>
                            <div style={styles.infoItem}>
                                <span style={styles.infoLabel}>Geçerlilik Tarihi</span>
                                <span style={styles.infoValue}>{formatDate(quote.validUntil)}</span>
                            </div>
                            <div style={styles.infoItem}>
                                <span style={styles.infoLabel}>Para Birimi</span>
                                <span style={styles.infoValue}>{quote.currency || 'TRY'}</span>
                            </div>
                            <div style={styles.infoItem}>
                                <span style={styles.infoLabel}>Oluşturan</span>
                                <span style={styles.infoValue}>{quote.createdBy || '-'}</span>
                            </div>
                        </div>

                        {/* Status Change */}
                        <div style={styles.statusBar}>
                            <span style={{ fontSize: '13px', color: '#6b7280', fontWeight: '500', alignSelf: 'center', marginRight: '4px' }}>Durum:</span>
                            {['Taslak', 'Gönderildi', 'Onaylandı', 'Reddedildi'].map(status => {
                                const isActive = quote.status === status;
                                const cfg = statusConfig[status];
                                return (
                                    <button
                                        key={status}
                                        style={{
                                            ...styles.statusBtn,
                                            backgroundColor: isActive ? cfg.bg : 'white',
                                            color: isActive ? cfg.color : '#6b7280',
                                            borderColor: isActive ? cfg.color : '#e2e8f0',
                                            fontWeight: isActive ? '600' : '400'
                                        }}
                                        onClick={() => handleStatusChange(status)}
                                    >
                                        {status}
                                    </button>
                                );
                            })}
                        </div>
                    </>
                )}
            </div>

            {/* Ürünler */}
            <div style={styles.box}>
                <div style={styles.sectionHeader}>
                    <h3 style={styles.sectionTitle}>
                        📦 Ürünler ({(isEditing ? editData.lineItems : quote.lineItems || []).length} kalem)
                    </h3>
                </div>

                {/* Add product row - only in edit mode */}
                {isEditing && (
                    <div style={styles.productRow}>
                        <div style={styles.formGroup}>
                            <label style={styles.label}>Ürün</label>
                            {currentItem.productId ? (
                                <div style={{ ...styles.selectedBadge, background: '#ecfdf5', color: '#047857' }}>
                                    <span>📦 {products.find(p => p.id === currentItem.productId)?.name || 'Ürün'}</span>
                                    <button style={{ ...styles.clearBtn, color: '#047857' }} onClick={() => { setCurrentItem(prev => ({ ...prev, productId: '', unitPrice: '' })); setProductSearch(''); }} title="Değiştir">✕</button>
                                </div>
                            ) : (
                                <div style={styles.autocompleteWrapper}>
                                    <input
                                        type="text"
                                        style={styles.input}
                                        placeholder="🔍 Ürün adı yazarak arayın..."
                                        value={productSearch}
                                        onChange={e => { setProductSearch(e.target.value); setShowProductDropdown(true); }}
                                        onFocus={() => setShowProductDropdown(true)}
                                        onBlur={() => setTimeout(() => setShowProductDropdown(false), 200)}
                                    />
                                    {showProductDropdown && productSearch.length > 0 && (
                                        <div style={styles.autocompleteDropdown}>
                                            {products.filter(p => p.name?.toLowerCase().includes(productSearch.toLowerCase())).length === 0 ? (
                                                <div style={styles.autocompleteEmpty}>Ürün bulunamadı</div>
                                            ) : (
                                                products.filter(p => p.name?.toLowerCase().includes(productSearch.toLowerCase())).map(p => (
                                                    <div
                                                        key={p.id}
                                                        style={styles.autocompleteItem}
                                                        onMouseDown={() => {
                                                            setCurrentItem(prev => ({ ...prev, productId: p.id, unitPrice: p.salePrice || 0 }));
                                                            setProductSearch('');
                                                            setShowProductDropdown(false);
                                                        }}
                                                        onMouseEnter={e => e.target.style.backgroundColor = '#f0fdf4'}
                                                        onMouseLeave={e => e.target.style.backgroundColor = 'white'}
                                                    >
                                                        📦 {p.name} {p.salePrice ? <span style={{ color: '#6b7280', marginLeft: '8px' }}>{p.salePrice} {sym}</span> : ''}
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                        <div style={styles.formGroup}>
                            <label style={styles.label}>Miktar</label>
                            <input type="number" min="1" style={styles.input} value={currentItem.quantity} onChange={e => setCurrentItem(prev => ({ ...prev, quantity: e.target.value }))} />
                        </div>
                        <div style={styles.formGroup}>
                            <label style={styles.label}>Birim Fiyat</label>
                            <input type="number" min="0" step="0.01" style={styles.input} value={currentItem.unitPrice} onChange={e => setCurrentItem(prev => ({ ...prev, unitPrice: e.target.value }))} placeholder="0.00" />
                        </div>
                        <div style={styles.formGroup}>
                            <label style={styles.label}>İskonto (%)</label>
                            <input type="number" min="0" max="100" style={styles.input} value={currentItem.discount} onChange={e => setCurrentItem(prev => ({ ...prev, discount: e.target.value }))} />
                        </div>
                        <div style={styles.formGroup}>
                            <label style={styles.label}>Tutar</label>
                            <div style={{ ...styles.input, display: 'flex', alignItems: 'center', color: '#059669', fontWeight: '600', backgroundColor: '#f0fdf4' }}>
                                {currentItem.unitPrice
                                    ? `${(Number(currentItem.quantity) * Number(currentItem.unitPrice) * (1 - Number(currentItem.discount) / 100)).toFixed(2)} ${sym}`
                                    : `0.00 ${sym}`}
                            </div>
                        </div>
                        <button onClick={handleAddEditItem} style={styles.addBtn}>+ Ekle</button>
                    </div>
                )}

                {/* Table */}
                <div style={styles.tableContainer}>
                    <table style={styles.table}>
                        <thead>
                            <tr>
                                <th style={styles.th}>#</th>
                                <th style={styles.th}>Ürün Adı</th>
                                <th style={styles.th} width="90">Miktar</th>
                                <th style={styles.th} width="120">Birim Fiyat</th>
                                <th style={styles.th} width="80">İskonto</th>
                                <th style={{ ...styles.th, textAlign: 'right' }} width="130">Toplam</th>
                                {isEditing && <th style={styles.th} width="50"></th>}
                            </tr>
                        </thead>
                        <tbody>
                            {(isEditing ? editData.lineItems : quote.lineItems || []).map((item, index) => (
                                <tr key={index}>
                                    <td style={{ ...styles.td, color: '#9ca3af' }}>{index + 1}</td>
                                    <td style={{ ...styles.td, fontWeight: '500' }}>{item.name}</td>
                                    <td style={styles.td}>
                                        {isEditing ? (
                                            <input type="number" min="1" style={styles.inlineInput} value={item.quantity}
                                                onChange={e => handleEditLineItemChange(index, 'quantity', e.target.value)} />
                                        ) : item.quantity}
                                    </td>
                                    <td style={styles.td}>
                                        {isEditing ? (
                                            <input type="number" min="0" step="0.01" style={styles.inlineInput} value={item.unitPrice}
                                                onChange={e => handleEditLineItemChange(index, 'unitPrice', e.target.value)} />
                                        ) : `${Number(item.unitPrice).toFixed(2)} ${sym}`}
                                    </td>
                                    <td style={styles.td}>
                                        {isEditing ? (
                                            <input type="number" min="0" max="100" style={{ ...styles.inlineInput, width: '60px' }} value={item.discount}
                                                onChange={e => handleEditLineItemChange(index, 'discount', e.target.value)} />
                                        ) : (
                                            item.discount > 0 ? <span style={{ color: '#ef4444', fontWeight: '500' }}>%{item.discount}</span> : '-'
                                        )}
                                    </td>
                                    <td style={{ ...styles.td, fontWeight: '600', textAlign: 'right' }}>
                                        {Number(item.total).toFixed(2)} {sym}
                                    </td>
                                    {isEditing && (
                                        <td style={styles.td}>
                                            <button onClick={() => handleRemoveEditItem(index)} style={styles.deleteBtn} title="Sil">🗑️</button>
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Totals */}
                <div style={styles.totalsWrapper}>
                    <div style={styles.totalsBox}>
                        {isEditing ? (
                            <>
                                <div style={styles.totalRow}>
                                    <span style={styles.totalLabel}>Ara Toplam:</span>
                                    <span style={{ fontWeight: '600' }}>{editSubTotal.toFixed(2)} {sym}</span>
                                </div>
                                <div style={styles.totalRow}>
                                    <span style={{ ...styles.totalLabel, display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        Genel İskonto:
                                        <input type="number" min="0" max="100" style={{ ...styles.inlineInput, width: '60px', textAlign: 'center' }}
                                            value={editData.discountRate} onChange={e => handleEditFieldChange('discountRate', Number(e.target.value) || 0)} />
                                        <span style={{ color: '#9ca3af' }}>%</span>
                                    </span>
                                    <span style={{ color: '#ef4444', fontWeight: '500' }}>-{editDiscountAmount.toFixed(2)} {sym}</span>
                                </div>
                                <div style={styles.totalRow}>
                                    <span style={{ ...styles.totalLabel, display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        KDV:
                                        <input type="number" min="0" max="100" style={{ ...styles.inlineInput, width: '60px', textAlign: 'center' }}
                                            value={editData.vatRate} onChange={e => handleEditFieldChange('vatRate', Number(e.target.value) || 0)} />
                                        <span style={{ color: '#9ca3af' }}>%</span>
                                    </span>
                                    <span>{editVatAmount.toFixed(2)} {sym}</span>
                                </div>
                                <div style={styles.grandTotalRow}>
                                    <span style={styles.grandTotalLabel}>GENEL TOPLAM:</span>
                                    <span style={styles.grandTotalValue}>{editGrandTotal.toFixed(2)} {sym}</span>
                                </div>
                            </>
                        ) : (
                            <>
                                <div style={styles.totalRow}>
                                    <span style={styles.totalLabel}>Ara Toplam:</span>
                                    <span style={{ fontWeight: '600' }}>{Number(quote.subTotal || 0).toFixed(2)} {sym}</span>
                                </div>
                                {quote.discountRate > 0 && (
                                    <div style={styles.totalRow}>
                                        <span style={styles.totalLabel}>Genel İskonto (%{quote.discountRate}):</span>
                                        <span style={{ color: '#ef4444', fontWeight: '500' }}>-{Number(quote.discountAmount || 0).toFixed(2)} {sym}</span>
                                    </div>
                                )}
                                <div style={styles.totalRow}>
                                    <span style={styles.totalLabel}>KDV (%{quote.vatRate || 0}):</span>
                                    <span>{Number(quote.vatAmount || 0).toFixed(2)} {sym}</span>
                                </div>
                                <div style={styles.grandTotalRow}>
                                    <span style={styles.grandTotalLabel}>GENEL TOPLAM:</span>
                                    <span style={styles.grandTotalValue}>{Number(quote.total || 0).toFixed(2)} {sym}</span>
                                </div>
                            </>
                        )}
                    </div>
                </div>

                {/* Notes (view mode) */}
                {!isEditing && quote.notes && (
                    <div style={styles.notesBox}>
                        <strong>📌 Notlar:</strong><br />
                        {quote.notes}
                    </div>
                )}
            </div>
        </div>
    );
}

export default PriceQuoteDetail;
